import { type ReactNode, useEffect, useState } from "react";
import { PausedScreen } from "@/features/status/PausedScreen";
import {
  fetchPlatformStatus,
  type PlatformStatus,
} from "@/lib/platform-status";

const NORMAL_POLL_MS = 30_000;
const PAUSED_POLL_MS = 10_000;

/**
 * Shows the "we're under attack" screen instead of the app while the platform
 * is paused, and brings the app back on its own when the pause ends. If the
 * status check itself fails the app is shown: a hiccup must never hide the site.
 */
export function PlatformStatusGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<PlatformStatus>({ state: "normal" });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = async () => {
      let next: PlatformStatus = { state: "normal" };
      try {
        next = await fetchPlatformStatus();
      } catch {
        // Fail open.
      }
      if (cancelled) return;
      setStatus(next);
      timer = setTimeout(
        () => void check(),
        next.state === "paused" ? PAUSED_POLL_MS : NORMAL_POLL_MS,
      );
    };
    void check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (status.state === "paused") {
    return <PausedScreen message={status.message} until={status.until} />;
  }
  return <>{children}</>;
}
